import Lottie, {
  LottieComponentProps,
  LottieRefCurrentProps,
} from "lottie-react";
import {
  forwardRef,
  Suspense,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import DASH_IS_LOADING from "./assets/Dash-is-Loading.json";

export enum LottieAnimationType {
  ONBOARDING_CHECK = "dash-onboarding-check",
  HIGH_FIVE = "high-five",
  DASH_IS_LOADING = "dash-is-loading",
  PIN_BROWSER_EXTENSION = "pin-browser-extension",
  CHAT = "chat",
  CHAT_INVERSE = "chat-inverse",
  CHAT_GREETING = "chat-greeting",
  FIND = "find",
  FIND_INVERSE = "find-inverse",
  ORGANIZE = "organize",
  ORGANIZE_INVERSE = "organize-inverse",
  EDIT_PDF = "edit-pdf",
  EDIT_PDF_INVERSE = "edit-pdf-inverse",
}

export type LottieAnimationTypeWithoutVariant = {
  [K in keyof typeof LottieAnimationType]: K extends `${string}_INVERSE`
    ? never
    : (typeof LottieAnimationType)[K];
}[keyof typeof LottieAnimationType];

type Props = Omit<LottieComponentProps, "animationData"> & {
  animationType: LottieAnimationType;
  onDataLoad?: () => void;
  fallback?: NonNullable<React.ReactNode> | null;
};

export type LottieAnimationApi = {
  lottieRef?: LottieRefCurrentProps;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizeAnimationImport(animationData: any): object {
  return animationData.default || animationData;
}

export const LottieAnimation = forwardRef(
  (
    {
      fallback,
      animationType,
      autoplay,
      loop,
      className,
      alt,
      onDataLoad,
    }: Props,
    ref,
  ) => {
    const lottieRef = useRef<LottieRefCurrentProps>(null);
    const animationData = useNormalizeLottieAnimationData(
      animationType,
      onDataLoad,
    );

    useImperativeHandle(ref, (): LottieAnimationApi => {
      return {
        lottieRef: lottieRef.current || undefined,
      };
    });

    if (!Object.keys(animationData)?.length) {
      return null;
    }

    return (
      <Suspense fallback={fallback ?? null}>
        <Lottie
          alt={alt}
          animationData={animationData}
          autoplay={autoplay}
          loop={loop}
          className={className}
          lottieRef={lottieRef}
        />
      </Suspense>
    );
  },
);
LottieAnimation.displayName = "LottieAnimation";

/**
 * This is a hook that normalizes the animation data for a given animation type.
 * It's used to ensure that the animation data is always normalized and that the animation is loaded correctly.normalized animation data.
 * isCancelled is used to cancel the in-flight import if the component is unmounted.
 * We also clear the animation data when starting a new load.
 */
function useNormalizeLottieAnimationData(
  animationType: LottieAnimationType,
  onDataLoad?: () => void,
) {
  const [animationData, setAnimationData] = useState({});

  useEffect(() => {
    let isCancelled = false;

    setAnimationData({});

    (async () => {
      let uninitializedAnimationData;

      /**
       * We can't pass in a variable to the URL otherwise an error is through about critical dependencies on the import
       * So this is the best way I could tell to dynamically import.
       * Pretty unhappy with this, but for now it works.
       */
      switch (animationType) {
        case LottieAnimationType.ONBOARDING_CHECK:
          uninitializedAnimationData = await import(
            "./assets/Dash-Onboarding-Check.json"
          );
          break;
        case LottieAnimationType.HIGH_FIVE:
          uninitializedAnimationData = await import("./assets/HighFive.json");
          break;
        case LottieAnimationType.PIN_BROWSER_EXTENSION:
          uninitializedAnimationData = await import(
            "./assets/Pin-Browser-Extension.json"
          );
          break;
        case LottieAnimationType.DASH_IS_LOADING:
          // Importing dash_is_loading statically since its used in the extension right on popup open. If we dynamically import it it takes longer to load than the popup page.
          uninitializedAnimationData = DASH_IS_LOADING;
          break;
        case LottieAnimationType.CHAT:
          uninitializedAnimationData = await import("./assets/Chat.json");
          break;
        case LottieAnimationType.CHAT_INVERSE:
          uninitializedAnimationData = await import(
            "./assets/Chat-Inverse.json"
          );
          break;
        case LottieAnimationType.CHAT_GREETING:
          uninitializedAnimationData = await import(
            "./assets/Dash-Chat-Greeting.json"
          );
          break;
        case LottieAnimationType.FIND:
          uninitializedAnimationData = await import("./assets/Find.json");
          break;
        case LottieAnimationType.FIND_INVERSE:
          uninitializedAnimationData = await import(
            "./assets/Find-Inverse.json"
          );
          break;
        case LottieAnimationType.ORGANIZE:
          uninitializedAnimationData = await import("./assets/Organize.json");
          break;
        case LottieAnimationType.ORGANIZE_INVERSE:
          uninitializedAnimationData = await import(
            "./assets/Organize-Inverse.json"
          );
          break;
        case LottieAnimationType.EDIT_PDF:
          uninitializedAnimationData = await import("./assets/Edit-PDF.json");
          break;
        case LottieAnimationType.EDIT_PDF_INVERSE:
          uninitializedAnimationData = await import(
            "./assets/Edit-PDF-Inverse.json"
          );
          break;
        default:
          break;
      }

      if (isCancelled) {
        return;
      }

      const normalizedAnimationData = normalizeAnimationImport(
        uninitializedAnimationData,
      );
      if (normalizedAnimationData) {
        if (onDataLoad) {
          onDataLoad();
        }

        setAnimationData(normalizedAnimationData);
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [animationType, onDataLoad]);

  return animationData;
}

export function getIconVariant(
  animationType: LottieAnimationTypeWithoutVariant,
  variant: "inverse" | "default",
) {
  if (variant === "default") {
    return animationType;
  }

  return (animationType + `-${variant}`) as LottieAnimationType;
}
